import { FileTextOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import DataTable from "../../../Components/DataTable/DataTable";
import api from "../../../api/api";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Button, message, Tag } from "antd";
import { getMyLeadStatusLabel } from "../../../Util/GetLeadStatusLabel";
import JalaliDateCell from "../../../Components/Leads/JalaliDateCell";
import PaidOrderTag from "../../../Components/Leads/PaidOrderTag";
import LeadActions from "../../../Components/Leads/LeadActions";
import LeadInfoModal from "../../../Components/Leads/LeadInfoModal";
import FirstReportCell from "../../../Components/Leads/FirstReportCell";
import styles from "./MyLeads.module.css";
import CheckPermission from "../../../Util/CheckPermission";
import LeadReportModal, {
  STATUS_OPTIONS,
  STATUS_MAP,
} from "../../../Components/Leads/LeadReportModal";
import ScenarioCell from "../../../Components/Leads/ScenarioCell";
import {
  SCENARIO_OPTIONS,
  mergeScenario,
} from "../../../Components/Leads/scenarios";
import ConvertNumbersToEn from "../../../Util/ConvertNumbersToEn";
import { getLeadTypeLabel } from "../../../Util/GetLeadTypeLabel";

const ReportStatusesCell = ({ record, onShowReport }) => {
  const statuses = record?.reportStatuses || [];
  const paidOrders =
    record?.hasPaidOrder && record?.paidOrders != null ? record.paidOrders : [];
  const hasTags = statuses.length > 0 || paidOrders.length > 0;

  return (
    <div className={styles.reportStatuses}>
      <Button
        type="default"
        icon={<FileTextOutlined />}
        onClick={() => onShowReport(record)}
        style={
          record?.reportCount > 0
            ? {
                backgroundColor: "#4A90D9",
                color: "#fff",
                borderColor: "#4A90D9",
              }
            : undefined
        }
      >
        گزارش
      </Button>
      {hasTags && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "flex-start",
          }}
        >
          {statuses.map((s) => (
            <Tag
              key={s}
              color={STATUS_MAP[s]?.color || "default"}
              style={{ margin: 0 }}
            >
              {STATUS_MAP[s]?.label || s}
            </Tag>
          ))}
          {paidOrders.map((order) => (
            <PaidOrderTag
              key={order.orderId || order.id}
              order={order}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MyLeads = ({ permissions, role }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestingRenewal, setRequestingRenewal] = useState(false);
  const renewalRequestRef = useRef(false);
  const [info, setInfo] = useState(null);
  const reportModalRef = useRef(null);

  const updateStatus = useCallback((lead, status) => {
    api
      .post("/leads/change-status/", { id: lead.id, status })
      .then(() => {
        setData((prevData) =>
          prevData.map((item) =>
            item.id === lead.id ? { ...item, status } : item,
          ),
        );
        message.success("وضعیت با موفقیت تغییر کرد");
      })
      .catch((error) => {
        message.error("خطا در تغییر وضعیت");
        console.error("Failed to update status:", error);
      });
  }, []);

  const showInfo = useCallback((lead) => {
    api
      .get("/leads/info/" + lead.lead?.id)
      .then((result) => {
        setInfo(result.data);
      })
      .catch((error) => {
        message.error("خطا در دریافت اطلاعات");
        console.error("Failed to get info:", error);
      });
  }, []);

  const showReport = useCallback((lead) => {
    reportModalRef.current?.open(
      lead.lead?.id || lead.id,
      lead.lead?.username || lead.username,
      lead.id,
    );
  }, []);

  const backToLeads = useCallback((lead) => {
    api
      .post("/leads/back-to-leads/", { id: lead.id })
      .then((response) => {
        if (response.data?.status == "success") {
          message.success(response.data.message);
          setData((prevData) => prevData.filter((item) => item.id !== lead.id));
        } else {
          message.error(response.data.message);
        }
      })
      .catch((error) => {
        message.error("خطا در ارسال درخواست");
        console.error("Failed to request lead:", error);
      });
  }, []);

  const lastQueryRef = useRef({ page: 1, pageSize: 100, filters: {} });

  const requestLead = useCallback(() => {
    api
      .get("/leads/request")
      .then((response) => {
        if (response.data?.status == "success") {
          message.success(response.data.message);
          const { page, pageSize, filters } = lastQueryRef.current;
          loadData(page, pageSize, filters);
        } else {
          message.error(response.data.message);
        }
      })
      .catch((error) => {
        message.error("خطا در ارسال درخواست");
        console.error("Failed to request lead:", error);
      });
  }, []);

  const requestRenewalLead = useCallback(() => {
    if (renewalRequestRef.current) return;
    renewalRequestRef.current = true;
    setRequestingRenewal(true);
    api
      .get("/leads/renewal-request")
      .then((response) => {
        if (response.data?.status == "success") {
          message.success(response.data.message);
          const { page, pageSize, filters } = lastQueryRef.current;
          loadData(page, pageSize, filters);
        } else {
          message.error(response.data.message);
        }
      })
      .catch((error) => {
        message.error("خطا در ارسال درخواست");
        console.error("Failed to request lead:", error);
      })
      .finally(() => {
        renewalRequestRef.current = false;
        setRequestingRenewal(false);
      });
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "موبایل",
        dataIndex: "username",
        searchable: true,
        width: 100,
        render: (_, record) => {
          const u = record.lead?.username;
          return (
            <span
              onClick={() => {
                navigator.clipboard.writeText(u);
                message.success("شماره موبایل در کلیپ بورد کپی شد");
              }}
            >
              {u}
            </span>
          );
        },
      },
      {
        title: "نام",
        dataIndex: "firstname",
        searchable: true,
        width: 64,
        render: (_, record) => record.lead?.firstname || "-",
      },
      {
        title: "نام خانوادگی",
        dataIndex: "lastname",
        searchable: true,
        width: 80,
        render: (_, record) => record.lead?.lastname || "-",
      },
      {
        title: "نوع لید",
        dataIndex: "lead_type",
        width: 72,
        render: (_, record) => getLeadTypeLabel(record.lead_type),
      },
      {
        title: "تاریخ ثبت نام",
        dataIndex: "lead",
        width: 96,
        render: (lead) => <JalaliDateCell lead={lead} />,
      },
      {
        title: "تاریخ اولین خرید",
        dataIndex: "firstBuyDate",
        width: 96,
        sorter: true,
        render: (value) => <JalaliDateCell date={value} />,
      },
      {
        title: "وضعیت",
        dataIndex: "status",
        filterMultiple: false,
        filters: [
          { text: "تماس گرفته نشده", value: "not_called" },
          { text: "تماس گرفته شده", value: "called" },
        ],
        width: 86,
        render: (status) => getMyLeadStatusLabel(status),
      },
      {
        title: "اولین گزارش",
        dataIndex: "firstReportDate",
        width: 96,
        render: (_, record) => <FirstReportCell record={record} />,
      },
      {
        title: "گزارشات",
        dataIndex: "reportStatuses",
        width: 120,
        filters: [
          ...STATUS_OPTIONS.map((s) => ({ text: s.label, value: s.value })),
          { text: "تگ مبلغ خریداری شده", value: "__paid__" },
        ],
        render: (_, record) => (
          <ReportStatusesCell record={record} onShowReport={showReport} />
        ),
      },
      {
        title: "سناریو",
        dataIndex: "reportScenarios",
        width: 76,
        filters: SCENARIO_OPTIONS.map((s) => ({
          text: s.label,
          value: s.value,
        })),
        render: (_, record) => <ScenarioCell record={record} />,
      },
      {
        title: "عملیات",
        width: 116,
        className: styles.actions,
        render: (_, record) => (
          <LeadActions
            record={record}
            onShowInfo={showInfo}
            onUpdateStatus={updateStatus}
            onBackToLeads={backToLeads}
          />
        ),
      },
    ],
    [updateStatus, showInfo, showReport, backToLeads],
  );

  const filtersBeautify = (fData = {}) => {
    const reportStatusValues = (fData.reportStatuses ?? []).filter(
      (v) => v !== "__paid__",
    );
    const hasPaidOrder = (fData.reportStatuses ?? []).includes("__paid__");
    return {
      ...(fData.username?.at(0)
        ? { mobile: ConvertNumbersToEn(fData.username.at(0)) }
        : {}),
      ...(fData.firstname?.at(0) ? { name: fData.firstname.at(0) } : {}),
      ...(fData.lastname?.at(0) ? { family: fData.lastname.at(0) } : {}),
      ...(fData.status?.at(0) ? { callStatus: fData.status.at(0) } : {}),
      ...(reportStatusValues.length
        ? { reportStatuses: reportStatusValues.join(",") }
        : {}),
      ...(fData.reportScenarios?.length
        ? { scenarios: fData.reportScenarios.join(",") }
        : {}),
      ...(hasPaidOrder ? { hasPaidOrder: "true" } : {}),
    };
  };

  const loadData = useCallback(
    (pageNumber = 1, pageSize = 100, filters = {}) => {
      lastQueryRef.current = { page: pageNumber, pageSize, filters };
      setLoading(true);
      api
        .get("/leads/my", { params: { pageNumber, pageSize, ...filters } })
        .then((res) => {
          setData(res.data.leads || []);
          setTotal(res.data.total || res.data.leads?.length || 0);
        })
        .catch((error) => {
          console.error("Failed to load leads:", error);
          message.error("خطا در بارگذاری لیدها");
          setData([]);
          setTotal(0);
        })
        .finally(() => setLoading(false));
    },
    [],
  );

  return (
    <div className={styles.leads}>
      <DataTable
        tableKey="my-leads"
        dynamicPagination
        serverSort
        showRowNumber
        scroll={{ x: 1040, y: "calc(100vh - 20rem)" }}
        recordsCount={total}
        filtersBeautify={filtersBeautify}
        header={{
          title: (
            <>
              لیدهای من
              {CheckPermission("leads_request", permissions) && (
                <Button
                  type="primary"
                  style={{ marginRight: 16 }}
                  onClick={() => requestLead()}
                >
                  درخواست لید جدید
                </Button>
              )}
              {CheckPermission("leads_renewal_request", permissions) && (
                <Button
                  type="primary"
                  style={{ marginRight: 16 }}
                  loading={requestingRenewal}
                  disabled={requestingRenewal}
                  onClick={() => requestRenewalLead()}
                >
                  درخواست لید تمدیدی
                </Button>
              )}
            </>
          ),
          avatar: { icon: <UsergroupAddOutlined />, size: "large" },
        }}
        userRole={role}
        onReset={loadData}
        columns={columns}
        dataSource={data}
        hideComment
        hideErrorReport
        setDataSource={setData}
        reload={loadData}
        path="/leads"
        permissionsPrefix="leads"
        loading={loading}
      />

      <LeadInfoModal
        info={info}
        loading={loading}
        onClose={() => setInfo(null)}
      />
      <LeadReportModal
        ref={reportModalRef}
        onReportChange={(leadId, reportDate, scenario) => {
          setData((prev) =>
            prev.map((item) =>
              (item.lead?.id || item.id) === leadId
                ? {
                    ...item,
                    reportCount: (item.reportCount || 0) + 1,
                    firstReportDate:
                      item.firstReportDate ||
                      (reportDate
                        ? new Date(reportDate).toISOString()
                        : item.firstReportDate),
                    reportScenarios: mergeScenario(
                      item.reportScenarios,
                      scenario,
                    ),
                  }
                : item,
            ),
          );
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  permissions: state.userData?.permissions || null,
  role: state.userData?.role || null,
});

export default connect(mapStateToProps)(MyLeads);
